<template>
	<section class="flex w-full min-w-0 flex-col text-ihh-midnight-gray" :class="compact ? 'gap-7 p-6' : 'gap-10'">
		<header class="flex flex-col gap-2.5">
			<h1 class="font-semibold leading-[1.1] tracking-[-0.02em]" :class="compact ? 'text-[28px]' : 'text-[44px]'">
				{{ result.ownLabel ? 'Your return is approved' : 'Your return label is ready' }}
			</h1>
			<p class="text-[19px] text-[#5E5863]">
				Estimated refund <strong class="font-semibold text-ihh-midnight-gray">{{ money(result.estimatedRefund) }}</strong>
			</p>
		</header>

		<div class="overflow-hidden rounded-2xl border border-[#ECE7E2] bg-white text-base tabular-nums shadow-[0_1px_2px_rgba(58,53,64,0.04),0_8px_24px_rgba(58,53,64,0.05)]">
			<div class="flex flex-col gap-3 border-b border-[#ECE7E2] px-7 py-[22px]">
				<div class="flex justify-between gap-4"><span class="text-[#5E5863]">RMA #</span><span class="font-semibold">{{ result.rmaNumber }}</span></div>
				<div class="flex justify-between gap-4"><span class="text-[#5E5863]">Order #</span><span class="font-semibold">{{ result.orderNumber }}</span></div>
			</div>
			<div class="flex flex-col gap-3 px-7 py-[22px]">
				<div v-if="hasAmount(result.itemTotal)" class="flex justify-between gap-4">
					<span class="text-[#5E5863]">Item total</span>
					<span>{{ money(result.itemTotal) }}</span>
				</div>
				<div v-if="!result.ownLabel" class="flex justify-between gap-4">
					<span class="text-[#5E5863]">UPS Ground label</span>
					<span>−{{ money(result.labelCost) }}</span>
				</div>
				<div v-if="Number(result.restockingFee) > 0" class="flex justify-between gap-4">
					<span class="text-[#5E5863]">Restocking fee ({{ restockingPercent }})</span>
					<span>−{{ money(result.restockingFee) }}</span>
				</div>
			</div>
			<div class="flex items-baseline justify-between gap-4 bg-[#FBF0EE] px-7 py-5">
				<span class="font-semibold">Estimated refund</span>
				<span class="text-[30px] font-semibold">{{ money(result.estimatedRefund) }}</span>
			</div>
		</div>

		<ol class="m-0 flex list-none flex-col border-t border-[#ECE7E2] p-0">
			<li class="flex gap-5 border-b border-[#ECE7E2] py-6">
				<span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ihh-dark-salmon text-base font-semibold text-ihh-midnight-gray">1</span>
				<div v-if="result.ownLabel" class="flex flex-col gap-1 pt-1">
					<h3 class="m-0 text-[19px] font-semibold">Write your RMA on the box</h3>
					<p class="m-0 text-base leading-normal text-[#5E5863]">
						Mark the package with RMA <strong class="font-semibold text-ihh-midnight-gray">{{ result.rmaNumber }}</strong> so the warehouse can match it to your order.
					</p>
				</div>
				<div v-else class="flex flex-col items-start gap-3.5 pt-1">
					<h3 class="m-0 text-[19px] font-semibold">Print the label</h3>
					<a
						v-if="result.labelUrl"
						:href="result.labelUrl"
						:download="labelFilename"
						target="_blank"
						rel="noopener noreferrer"
						class="inline-flex h-12 items-center rounded-[10px] bg-ihh-dark-salmon px-6 text-base font-semibold text-ihh-midnight-gray transition-colors duration-150 hover:bg-[#DD8678]"
						:class="downloading ? 'pointer-events-none opacity-60' : ''"
						:aria-disabled="downloading ? 'true' : 'false'"
						@click="beginDownload"
					>
						{{ downloading ? 'Downloading…' : 'Download label' }}
					</a>
					<p v-else class="text-[15px] leading-snug text-red-700">We couldn't generate your label. Please contact help@inhishands.com.</p>
				</div>
			</li>

			<li class="flex gap-5 border-b border-[#ECE7E2] py-6">
				<span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#FBE9E5] text-base font-semibold text-[#B65A4E]">2</span>
				<div v-if="result.ownLabel" class="flex min-w-0 flex-col items-start gap-2.5 pt-1">
					<h3 class="m-0 text-[19px] font-semibold">Ship it with any carrier</h3>
					<p class="m-0 text-base leading-normal text-[#5E5863]">Send the package to our warehouse at your own expense.</p>
					<div class="flex w-full max-w-full flex-wrap items-start gap-5 rounded-[10px] border border-[#ECE7E2] px-4 py-3.5 sm:w-auto">
						<p class="text-base leading-snug">
							In His Hands<br />
							325 Cole Dr.<br />
							Liberty Hill, TX 78642
						</p>
						<button type="button" class="h-9 shrink-0 whitespace-nowrap rounded-lg border border-ihh-dark-salmon bg-white px-3.5 text-sm font-semibold text-ihh-midnight-gray transition-colors duration-150 hover:bg-[#FBE9E5]" @click="copyAddress">
							{{ copied ? 'Copied' : 'Copy address' }}
						</button>
					</div>
				</div>
				<div v-else class="flex flex-col gap-1.5 pt-1">
					<h3 class="m-0 text-[19px] font-semibold">Drop it off with UPS</h3>
					<p class="m-0 text-base leading-normal text-[#5E5863]">
						Pack items securely. Tape the label to the box.<template v-if="result.trackingNumber">
							Tracking <span class="break-words font-[ui-monospace,SFMono-Regular,Menlo,monospace] text-[15px] text-ihh-midnight-gray">{{ result.trackingNumber }}</span>
						</template>
					</p>
					<a
						href="https://www.ups.com/us/en/business-solutions/pickup-dropoff-options"
						target="_blank"
						rel="noopener noreferrer"
						class="text-base text-[#C9665A] underline underline-offset-[3px] hover:text-[#A84E43]"
					>Find a UPS store or dropoff location</a>
				</div>
			</li>

			<li class="flex gap-5 border-b border-[#ECE7E2] py-6">
				<span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#FBE9E5] text-base font-semibold text-[#B65A4E]">3</span>
				<div class="flex flex-col gap-1 pt-1">
					<h3 class="m-0 text-[19px] font-semibold">Get your refund</h3>
					<p class="m-0 text-base leading-normal text-[#5E5863]">Most returns are processed within a week of arriving at our warehouse. Only items selected for return can be refunded.</p>
				</div>
			</li>
		</ol>

		<button type="button" class="self-center text-base text-[#C9665A] underline underline-offset-[3px] hover:text-[#A84E43]" @click="$emit('restart')">
			Start another return
		</button>
	</section>
</template>

<script>
const COPY_TEXT = 'In His Hands, 325 Cole Dr., Liberty Hill, TX 78642'
const COPY_RESET_MS = 1600

export default {
	name: 'ReturnConfirmation',
	props: {
		result: { type: Object, required: true },
		compact: { type: Boolean, default: false },
	},
	emits: ['restart'],
	data() {
		return {
			copied: false,
			downloading: false,
			copyTimer: null,
			downloadTimer: null,
		}
	},
	computed: {
		restockingPercent() {
			const rate = Number(this.result?.restockingRate)
			if (!Number.isFinite(rate) || rate <= 0) return '2%'
			const pct = rate <= 1 ? rate * 100 : rate
			const rounded = Math.round(pct * 100) / 100
			return `${rounded}%`
		},
		labelFilename() {
			const name = String(this.result?.rmaNumber || this.result?.orderNumber || 'label').replace(/[^\w.-]+/g, '')
			return `return-label-${name || 'label'}.pdf`
		},
	},
	beforeUnmount() {
		clearTimeout(this.copyTimer)
		clearTimeout(this.downloadTimer)
	},
	methods: {
		money(value) {
			return `$${Number(value || 0).toFixed(2)}`
		},
		hasAmount(value) {
			return value !== undefined && value !== null && value !== ''
		},
		async copyAddress() {
			try {
				if (!navigator.clipboard?.writeText) return
				await navigator.clipboard.writeText(COPY_TEXT)
				this.copied = true
				clearTimeout(this.copyTimer)
				this.copyTimer = setTimeout(() => {
					this.copied = false
				}, COPY_RESET_MS)
			} catch {
				// Clipboard access can be unavailable.
			}
		},
		beginDownload(event) {
			if (this.downloading) {
				event.preventDefault()
				return
			}
			this.downloading = true
			clearTimeout(this.downloadTimer)
			this.downloadTimer = setTimeout(() => {
				this.downloading = false
			}, 1500)
		},
	},
}
</script>
